import { CASCADE_ENUM } from '../../../../services/enum.service';
import {
  ActionCellRenderer,
  ActionTypes,
} from '../../../../utils/custom-renderer/action-cell-renderer';
import {
  HyperlinkStaticCellRenderer,
  IGridHyperlink,
} from '../../../../utils/custom-renderer/hyperlink-cell-renderer';
import { CONTRACT_TYPE, ICONS, PRODUCT_TYPE, PROFILE_RIGHTS } from '../../../../utils';
import {
  getPriceRange,
  getFormattedNumber,
  getFormattedDate,
  getStatusColour,
  getFillColorFromProjectStatus,
  getPriceValue,
  getCreditProfileValue,
  DEFAULT_REC_SETTLEMENT_POINT,
} from '../../../../utils/formatting';
import {
  CUSTOM_HEADER_TOOLTIPS,
  filterGreaterOrEqualNumeric,
  filterLessOrEqualNumeric,
  filterNumericRange,
  headerWithTooltipColParams,
} from '../../../../utils/grid';
import type { BasketListing } from './basket-listing';
import { getTechnologyIconName } from '../../../../styles';

function getRowStyle(params: any) {
  const response = {};
  if (params.data.IS_STATISTICS) {
    response['background-color'] = 'rgba(11, 214, 146, 0.1)';
  } else if (!params.data.PROJECT_ID) {
    response['font-size'] = '12px';
  }
  return response;
}

function getStatusStyle(params: any) {
  const response = {};

  if (params.data.IS_STATISTICS) {
    response['background-color'] = 'rgba(11, 214, 146, 0.1)';
  }

  if (params.data?.STATUS) {
    response['color'] = getStatusColour(params.data?.STATUS);
  }

  return response;
}

export enum COLUMN_FIELDS {
  PROJECT_NAME = 'PROJECT_NAME',
  TICKER = 'TICKER',
  UNIQUE_ID = 'UNIQUE_ID',
  TECHNOLOGY = 'TECHNOLOGY',
  ISO = 'ISO',
  SETTLEMENT_POINT = 'SETTLEMENT_POINT',
  VOLUME_YEAR = 'VOLUME_YEAR',
  VOLUME = 'VOLUME',
  FIXED_PRICE_RANGE = 'PRICE',
  START_DATE = 'START_DATE',
  TENOR_MONTHS = 'TENOR_MONTHS',
  COLLATERAL = 'COLLATERAL',
  IMPLIED_CARBON_PRICE_RANGE = 'COLLATERAL',
  STATUS = 'STATUS',
  ACTIONS = 'ACTIONS',
}

export function BASKET_LISTING_COLUMNS(context: BasketListing) {
  return [
    {
      field: COLUMN_FIELDS.PROJECT_NAME,
      headerName: 'Project',
      minWidth: 140,
      flex: 2,
      filter: false,
      suppressMenu: true,
      sortable: false,
      cellStyle: (params) => getRowStyle(params),
      cellRenderer: HyperlinkStaticCellRenderer,
      cellRendererParams: (params) => {
        const obj = params.data;

        if (!obj.PROJECT_NAME) return null;

        const iconList: Array<{ icon: ICONS; width; height; fill? }> = [];

        const hyperlinkParams: IGridHyperlink = {
          value: obj.PROJECT_NAME,
          id: null,
          vppaId: null,
          iconList: [],
        };
        if (obj.PROJECT_ID) {
          hyperlinkParams.id = obj.PROJECT_ID;

          const iconName = getTechnologyIconName(obj.TECHNOLOGY);
          iconList.push({
            icon: iconName,
            width: '13px',
            height: '16px',
            fill: getFillColorFromProjectStatus(obj.PROJECT_OPERATIONAL_STATUS),
          });
        } else if (!obj.IS_STATISTICS) {
          iconList.push({
            icon: ICONS.CIRCLED_ERROR,
            width: '16px',
            height: '16px',
          });
        }
        hyperlinkParams.iconList = iconList;
        return hyperlinkParams;
      },
      lockPosition: 'left',
      lockPinned: true,
      ...headerWithTooltipColParams(CUSTOM_HEADER_TOOLTIPS.ICON_LEGEND),
    },
    {
      field: COLUMN_FIELDS.TICKER,
      headerName: 'Ticker',
      lockPinned: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
      cellStyle: (params) => getRowStyle(params),
    },
    {
      field: COLUMN_FIELDS.UNIQUE_ID,
      headerName: 'Unique ID',
      lockPinned: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
      cellStyle: (params) => getRowStyle(params),
    },
    {
      field: COLUMN_FIELDS.TECHNOLOGY,
      headerName: 'Technology',
      lockPinned: true,
      filter: 'agSetColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
      valueGetter: (params) => {
        return context.enumService.getEnumTranslation(
          CASCADE_ENUM.TECHNOLOGY,
          params.data.TECHNOLOGY,
        );
      },
      cellStyle: (params) => getRowStyle(params),
    },
    {
      field: COLUMN_FIELDS.ISO,
      headerName: 'ISO',
      lockPinned: true,
      filter: 'agSetColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
      cellStyle: (params) => getRowStyle(params),
    },
    {
      field: COLUMN_FIELDS.SETTLEMENT_POINT,
      headerName: 'Settlement Point',
      lockPinned: true,
      filter: 'agSetColumnFilter',
      menuTabs: ['filterMenuTab'],
      valueFormatter: (params) =>
        params.data.CONTRACT_TYPE === CONTRACT_TYPE.REC
          ? DEFAULT_REC_SETTLEMENT_POINT
          : params.data.SETTLEMENT_POINT,
      filterValueGetter: (params) =>
        params.data.CONTRACT_TYPE === CONTRACT_TYPE.REC
          ? DEFAULT_REC_SETTLEMENT_POINT
          : params.data.SETTLEMENT_POINT,
      flex: 1,
      cellStyle: (params) => getRowStyle(params),
    },
    {
      field: COLUMN_FIELDS.VOLUME_YEAR,
      headerName: 'Volume (MWh/year)',
      lockPinned: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
      cellStyle: (params) => getRowStyle(params),
      valueFormatter: (params) => {
        if (params.data.VOLUME_YEAR) {
          return getFormattedNumber(params.data.VOLUME_YEAR);
        }
      },
    },
    {
      field: COLUMN_FIELDS.VOLUME,
      headerName: 'Volume (MW)',
      lockPinned: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
      cellStyle: (params) => getRowStyle(params),
      valueGetter: (params) => {
        if (params.data.VOLUME) {
          return getFormattedNumber(params.data.VOLUME);
        }
      },
    },
    {
      field: COLUMN_FIELDS.FIXED_PRICE_RANGE,
      headerName: 'Fixed Price Range',
      lockPinned: true,
      flex: 2,
      filter: 'agNumberColumnFilter',
      menuTabs: ['filterMenuTab'],
      filterParams: {
        filterOptions: [
          {
            displayKey: 'lessThanOrEqual',
            displayName: '<= Upper Bound',
            predicate: ([fv1], cellValue) => filterLessOrEqualNumeric(fv1, cellValue),
            numberOfInputs: 1,
          },
          {
            displayKey: 'greaterThanOrEqual',
            displayName: '>= Lower Bound',
            predicate: ([fv1], cellValue) => filterGreaterOrEqualNumeric(fv1, cellValue),
            numberOfInputs: 1,
          },
          {
            displayKey: 'inRange',
            displayName: 'In range',
            predicate: ([fv1, fv2], cellValue) => filterNumericRange([fv1, fv2], cellValue),
            numberOfInputs: 2,
          },
        ],
        suppressAndOrCondition: true,
      },
      valueGetter: (params) => {
        return [params.data.FIXED_PRICE_LOWER_BOUND, params.data.FIXED_PRICE_UPPER_BOUND];
      },
      valueFormatter: (params) => {
        if (!params.data.RFQ_ID) {
          return getPriceValue(params.data.FIXED_PRICE_LOWER_BOUND, '', '$');
        }
        if (params.data.PRODUCT_TYPE === PRODUCT_TYPE.RECS) {
          return getPriceValue(params.data.FIXED_PRICE_LOWER_BOUND, '', '$');
        }
        return getPriceRange(
          getPriceValue(params.data.FIXED_PRICE_LOWER_BOUND),
          getPriceValue(params.data.FIXED_PRICE_UPPER_BOUND),
        );
      },
      cellStyle: (params) => getRowStyle(params),
    },
    {
      field: COLUMN_FIELDS.START_DATE,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      headerName: 'Start Date',
      lockPinned: true,
      flex: 1,
      valueGetter: (params) => getFormattedDate(params.data.START_DATE),
      cellStyle: (params) => getRowStyle(params),
    },
    {
      field: COLUMN_FIELDS.TENOR_MONTHS,
      headerName: 'Tenor (mo)',
      lockPinned: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
      cellStyle: (params) => getRowStyle(params),
      valueFormatter: (params) => {
        if (params.data.TENOR_MONTHS) {
          return getFormattedNumber(params.data.TENOR_MONTHS);
        }
      },
    },
    {
      field: COLUMN_FIELDS.COLLATERAL,
      headerName: 'Credit Profile ($/MW)',
      lockPinned: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
      minWidth: 80,
      sortable: false,
      cellStyle: (params) => getRowStyle(params),
      valueGetter: (params) => {
        if (!params.data.RFQ_ID) {
          return '';
        }
        return getCreditProfileValue(params.data.RFQ_CREDIT_PROFILE, params.data.COLLATERAL);
      },
      ...headerWithTooltipColParams(CUSTOM_HEADER_TOOLTIPS.CREDIT_PROFILE),
    },
    {
      field: COLUMN_FIELDS.IMPLIED_CARBON_PRICE_RANGE,
      headerName: 'Implied Carbon Price Range',
      lockPinned: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 2,
      valueGetter: (params) => {
        if (!params.data.RFQ_ID) {
          return getPriceValue(params.data.IMPLIED_CARBON_PRICE_MIN, '', '$');
        }
        return getPriceRange(
          getPriceValue(params.data.IMPLIED_CARBON_PRICE_MIN),
          getPriceValue(params.data.IMPLIED_CARBON_PRICE_MAX),
        );
      },
      cellStyle: (params) => getRowStyle(params),
    },
    {
      field: COLUMN_FIELDS.STATUS,
      flex: 1,
      headerName: 'Status',
      lockPinned: true,
      filter: 'agSetColumnFilter',
      menuTabs: ['filterMenuTab'],
      valueGetter: (params) => {
        if (params.data.RFQ_ID) {
          return context.enumService.getEnumTranslation(
            CASCADE_ENUM.RFQ_STATUS,
            params.data.STATUS,
          );
        } else if (params.data.PROJECT_ID) {
          return context.enumService.getEnumTranslation(
            CASCADE_ENUM.PROJECT_STATUS,
            params.data.STATUS,
          );
        }

        return '';
      },
      cellStyle: (params) => getStatusStyle(params),
    },
    {
      field: COLUMN_FIELDS.ACTIONS,
      headerName: '',
      suppressMenu: true,
      minWidth: 200,
      maxWidth: 200,
      flex: 2,
      suppressRowClickSelection: true,
      pinned: 'right',
      lockPinned: true,
      cellStyle: (params) => getRowStyle(params),
      cellRenderer: ActionCellRenderer,
      cellRendererParams: (params) => {
        if (!params.data.RFQ_ID) return null;
        if (context.selectedRfq === params.data.RFQ_ID) {
          params.node.setSelected(true);
        }
        let actions = [];
        if (context.authService.validateProfile(PROFILE_RIGHTS.UPSERT_NEGOTIATION)) {
          const documentBtn = {
            name: 'Propose Contract',
            callback: (detail) => {
              params.node.setSelected(true);
            },
            type: ActionTypes.CREATE,
            actionName: 'propose-vppa',
            disabled: context.executedIds.includes(params.data.RFQ_ID),
          };
          actions.push(documentBtn);
        }
        const enabled = true;
        return {
          actions,
          enabled,
        };
      },
    },
  ];
}
